import { z } from 'zod';
import { Role } from './auth.js';
import { ApplicationStatus } from './participants.js';
import { Password } from './users.js';
/** GET /testing/status. The route exists only when the API runs with TESTING_MODE=true (docs/testing-mode.md). */
export const TestingStatus = z.object({
    testingMode: z.literal(true),
    /** The caller may use the Testing portal (`testing:admin`, the test admin role). */
    admin: z.boolean(),
    /** The caller's account is a Tester (`iam.users.is_tester`), so it may switch its own role and organisation (T3). */
    tester: z.boolean(),
});
export const TestUserOrganisation = z.object({
    id: z.string(),
    legalName: z.string(),
    abn: z.string(),
    status: z.string(),
});
/** An account the Testing portal created or imported, with its current roles and organisation. */
export const TestUser = z.object({
    id: z.string(),
    username: z.string(),
    displayName: z.string(),
    email: z.string().nullable(),
    emailVerified: z.boolean(),
    tester: z.boolean(),
    roles: z.array(Role),
    /** The linked participant; null for CER roles and applicants without one. */
    organisation: TestUserOrganisation.nullable(),
    disabled: z.boolean(),
    onboardedAt: z.string(),
});
export const TestUserList = z.object({ items: z.array(TestUser) });
/** The roles a tester can switch to. Never test_admin. */
export const TesterRole = z.enum(['cer_admin', 'cer_officer', 'participant_admin', 'participant_user', 'applicant']);
export const TesterOrganisation = z.object({ id: z.string(), legalName: z.string(), abn: z.string() });
/** An application still in flight (draft, submitted, under assessment or returned), which a tester may take over as applicant. */
export const TesterApplication = z.object({
    id: z.string(),
    participantId: z.string(),
    legalName: z.string(),
    status: ApplicationStatus,
    applicantUserId: z.string(),
    applicantName: z.string(),
    updatedAt: z.string(),
});
/** GET /testing/me/options: what the switcher offers. Organisations are the approved participants. */
export const TesterOptions = z.object({
    organisations: z.array(TesterOrganisation),
    applications: z.array(TesterApplication),
    /** The open application the tester holds, if any. Switching to applicant then offers: continue it, or withdraw it and begin a new one. */
    held: TesterApplication.nullable(),
});
/**
 * POST /testing/me/identity. Replaces the tester's roles and organisation; the reply is a new sign-in (LoginResponse).
 * Applicant with an applicationId takes over (or continues) that application; without one, the tester becomes a new applicant.
 * withdrawHeld withdraws the open application the tester holds first, so they can begin a new one.
 */
export const IdentitySwitchRequest = z.discriminatedUnion('role', [
    z.object({ role: z.literal('cer_admin') }).strict(),
    z.object({ role: z.literal('cer_officer') }).strict(),
    z.object({ role: z.literal('participant_admin'), participantId: z.string().min(1).max(100) }).strict(),
    z.object({ role: z.literal('participant_user'), participantId: z.string().min(1).max(100) }).strict(),
    z.object({ role: z.literal('applicant'), applicationId: z.string().uuid().optional(), withdrawHeld: z.literal(true).optional() }).strict(),
]);
export const TestUsername = z
    .string()
    .trim()
    .min(3)
    .max(120)
    .regex(/^[a-z0-9][a-z0-9._@+-]*$/i, 'letters, digits and . _ @ + - only');
export const TestUserEmail = z.string().trim().toLowerCase().email().max(254);
/** No line breaks or tabs, so every exported row stays on one CSV line. */
export const TestDisplayName = z
    .string()
    .trim()
    .min(1)
    .max(200)
    .regex(/^[^\r\n\t]*$/, 'no line breaks or tabs');
/**
 * POST /testing/users (T2). Participant roles need an approved participantId; the other roles take none.
 * The user needs a way to sign in: an initial password, or an invitation email with a set-password link.
 */
export const TestUserCreate = z
    .object({
    username: TestUsername,
    displayName: TestDisplayName,
    email: TestUserEmail.optional(),
    role: TesterRole,
    participantId: z.string().trim().min(1).max(100).optional(),
    tester: z.boolean(),
    password: Password.optional(),
    sendInvitation: z.boolean(),
})
    .strict()
    .superRefine((u, ctx) => {
    const participantRole = u.role === 'participant_admin' || u.role === 'participant_user';
    if (participantRole && !u.participantId)
        ctx.addIssue({ code: 'custom', path: ['participantId'], message: 'participant roles need an organisation' });
    if (!participantRole && u.participantId)
        ctx.addIssue({ code: 'custom', path: ['participantId'], message: `${u.role} takes no organisation` });
    if (u.sendInvitation && !u.email)
        ctx.addIssue({ code: 'custom', path: ['email'], message: 'an invitation needs an email address' });
    if (!u.sendInvitation && !u.password)
        ctx.addIssue({ code: 'custom', path: ['password'], message: 'set a password or send an invitation' });
});
/** The CSV upload's columns, in order. organisation is a participant id or ABN (participant roles only); tester is true/false/yes/no. */
export const TEST_USER_CSV_COLUMNS = ['username', 'displayName', 'email', 'role', 'organisation', 'tester'];
export const TEST_USER_CSV_MAX_ROWS = 500;
/** One CSV row after its cells are checked. Every row needs an email, because CSV users get their password through an invitation. */
export const TestUserCsvRow = z
    .object({
    username: TestUsername,
    displayName: TestDisplayName,
    email: TestUserEmail,
    role: TesterRole,
    organisation: z.string().trim().max(100),
    tester: z
        .string()
        .trim()
        .toLowerCase()
        .pipe(z.enum(['true', 'false', 'yes', 'no'], { errorMap: () => ({ message: 'true, false, yes or no' }) })),
})
    .superRefine((r, ctx) => {
    const participantRole = r.role === 'participant_admin' || r.role === 'participant_user';
    if (participantRole && !r.organisation)
        ctx.addIssue({ code: 'custom', path: ['organisation'], message: 'participant roles need an organisation' });
    if (!participantRole && r.organisation)
        ctx.addIssue({ code: 'custom', path: ['organisation'], message: `${r.role} takes no organisation` });
});
/** POST /testing/users/import-csv: every row is checked first, and nothing is created if any row fails (422 lists each problem). Every user created gets an invitation. */
export const TestUserCsvImport = z.object({ csv: z.string().min(1).max(1_000_000) }).strict();
export const TestUserCsvResult = z.object({ created: z.array(TestUser) });
/**
 * T4 export (GET /testing/export) and import columns, in order. roles and the organisation columns are for
 * reference only: an import gives every user cer_officer and no organisation.
 */
export const TEST_USER_EXPORT_COLUMNS = [
    'username',
    'displayName',
    'email',
    'emailVerified',
    'tester',
    'roles',
    'organisationId',
    'organisationAbn',
    'organisationName',
    'disabled',
    'passwordHash',
];
/** POST /testing/import. apply=false previews; apply=true makes every change in one transaction, or none. Problems are a 422 either way. */
export const TestUserImportRequest = z.object({ csv: z.string().min(1).max(5_000_000), apply: z.boolean() }).strict();
export const TestUserImportRow = z.object({
    line: z.number().int(),
    username: z.string(),
    displayName: z.string(),
    email: z.string().nullable(),
    tester: z.boolean(),
    disabled: z.boolean(),
    /** create: a new account. update: an existing portal-added account with that username. */
    action: z.enum(['create', 'update']),
});
export const TestUserImportResult = z.object({
    applied: z.boolean(),
    creates: z.number().int(),
    updates: z.number().int(),
    rows: z.array(TestUserImportRow),
});
//# sourceMappingURL=testing.js.map